import { useMemo, useState, useCallback } from "react"
import _ from "lodash"

import dayjs from "config/dayjs"

import { useGeoLocation } from "providers/geolocation"
import { useVenue } from "providers/venue"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useVenueController } from "hooks/venue/useVenueController"

import { Q_VENUE } from "constant"

export const useEvent = () => {
  const { events, venues, isMultiVenueProject } = useVenue()
  const navigate = useNavigate()
  const { locationVenue } = useGeoLocation()
  const locationVenueId = _.get(locationVenue, "id")
  const { viewingVenue: venueFilter } = useVenueController(locationVenueId)
  const currentTime = dayjs()
  const [searchParams] = useSearchParams()
  const [activeGroupEvent, setActiveGroupEvent] = useState("current")

  const changeVenueFilter = useCallback(
    (venueId) => {
      if (venueId) searchParams.set(Q_VENUE, venueId)
      navigate(`/events/?${searchParams.toString()}`, { replace: true })
    },
    [navigate, searchParams]
  )

  const sortEventsByDate = useCallback((events) => {
    return events.sort(
      (firstEvent, secondEvent) =>
        dayjs(firstEvent.properties.start_date).valueOf() -
        dayjs(secondEvent.properties.start_date).valueOf()
    )
  }, [])

  const currentEvents = useMemo(
    () =>
      events.filter((event) => {
        const { properties } = event
        const startDate = dayjs(properties?.start_date)
        const endDate = dayjs(properties?.end_date)

        return currentTime.isAfter(startDate) && currentTime.isBefore(endDate)
      }),
    [events, currentTime]
  )

  const viewingVenueCurrentEvent = useMemo(
    () =>
      currentEvents?.filter(
        ({ properties }) => properties.venue_id === venueFilter
      ),
    [currentEvents, venueFilter]
  )

  const upcomingEvents = useMemo(
    () =>
      events.filter((event) => {
        const { properties } = event
        const startDate = dayjs(properties?.start_date)
        return currentTime.isBefore(startDate)
      }),
    [events, currentTime]
  )

  const viewingVenueUpcomingEvents = useMemo(
    () =>
      upcomingEvents?.filter(
        ({ properties }) => properties.venue_id === venueFilter
      ),
    [upcomingEvents, venueFilter]
  )

  const groupEvents = useMemo(
    () => ({
      current: sortEventsByDate(viewingVenueCurrentEvent),
      upcoming: sortEventsByDate(viewingVenueUpcomingEvents),
    }),
    [viewingVenueCurrentEvent, viewingVenueUpcomingEvents, sortEventsByDate]
  )

  const handleChange = useCallback((e, eventGroup) => {
    if (eventGroup === null) return
    setActiveGroupEvent(eventGroup)
  }, [])

  return {
    venues,
    venueFilter,
    locationVenueId,
    isMultiVenueProject,
    groupEvents,
    activeGroupEvent,
    handleChange,
    changeVenueFilter,
  }
}
